import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  createTeacherSupportRequest,
  getActiveTrialInstancesBySchool,
  getSchools,
  getTrialBySchoolId
} from "../services/TeacherAPIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherExtendTrialModal.css";

const ANOTHER_REASON = "Another reason";

const EXTEND_TRIAL_REASONS = [
  {
    value: "We came back after summer and need another look",
    detail: "You want more time to review ALPACA with your school."
  },
  {
    value: "We have not had time to screen yet",
    detail: "You need more time before making a decision."
  },
  {
    value: ANOTHER_REASON,
    detail: "Add a short note with your request."
  }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const getSchoolRows = (schoolsResponse) => {
  if (Array.isArray(schoolsResponse)) {
    return schoolsResponse;
  }

  if (Array.isArray(schoolsResponse?.data)) {
    return schoolsResponse.data;
  }

  return [];
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return normaliseText(value);
  }

  return new Intl.DateTimeFormat("en-IE", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(date);
};

const getTrialRows = (trialResponse) => {
  if (Array.isArray(trialResponse)) {
    return trialResponse;
  }

  if (Array.isArray(trialResponse?.data)) {
    return trialResponse.data;
  }

  return trialResponse ? [trialResponse] : [];
};

const getTrialEndDate = (trial) =>
  trial?.end_date ||
  trial?.endDate ||
  trial?.trial_end_date ||
  trial?.trialEndDate ||
  trial?.trialEndsOn ||
  trial?.trial_ends_on ||
  trial?.expires_at ||
  trial?.expiresAt ||
  trial?.expiryDate;

const normaliseTrialDetails = (school, trialRows) => {
  const activeTrial = trialRows.find((trial) => trial?.active !== false && trial?.expired !== true) || trialRows[0] || null;

  return {
    schoolName: normaliseText(school?.name || school?.schoolName || school?.school_name),
    trialEndsOn: formatDate(getTrialEndDate(activeTrial)),
    status: normaliseText(activeTrial?.status || (activeTrial?.expired ? "Expired" : activeTrial ? "Active" : ""))
  };
};

const getMatchingReason = (reason) => {
  const normalisedReason = normaliseComparableText(reason);

  return (
    EXTEND_TRIAL_REASONS.find(
      (reasonOption) => normaliseComparableText(reasonOption.value) === normalisedReason
    )?.value || ""
  );
};

const normalisePrefill = (prefill) => {
  const schoolName = normaliseText(prefill?.schoolName || prefill?.school_name);
  const trialEndsOn = normaliseText(prefill?.trialEndsOn || prefill?.trial_ends_on);
  const reason = normaliseText(prefill?.reason);
  const note = normaliseText(prefill?.note);
  const matchingReason = getMatchingReason(reason);

  if (!reason) {
    return {
      schoolName,
      trialEndsOn,
      reason: "",
      note
    };
  }

  if (matchingReason) {
    return {
      schoolName,
      trialEndsOn,
      reason: matchingReason,
      note
    };
  }

  return {
    schoolName,
    trialEndsOn,
    reason: ANOTHER_REASON,
    note: note || reason
  };
};

function TeacherExtendTrialModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  schoolName = "",
  trialEndsOn = "",
  onRequestExtension = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null,
  onBackToTrialAccess = null
}) {
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const [stage, setStage] = useState("request");
  const [selectedReason, setSelectedReason] = useState("");
  const [anotherReasonNote, setAnotherReasonNote] = useState("");
  const [completedRequest, setCompletedRequest] = useState(null);
  const [trialDetails, setTrialDetails] = useState({ schoolName: "", trialEndsOn: "", status: "" });
  const [isLoadingTrialDetails, setIsLoadingTrialDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);
  const [error, setError] = useState("");

  const displayedSchoolName =
    trialDetails.schoolName || prefillContext.schoolName || normaliseText(schoolName) || "Your school";
  const displayedTrialEndsOn =
    trialDetails.trialEndsOn || prefillContext.trialEndsOn || normaliseText(trialEndsOn) || "Not shown";
  const trimmedNote = anotherReasonNote.trim().replace(/\s+/g, " ");
  const selectedReasonOption = EXTEND_TRIAL_REASONS.find(
    (reasonOption) => reasonOption.value === selectedReason
  );
  const canRequest =
    Boolean(selectedReason && !isSubmitting) &&
    (selectedReason !== ANOTHER_REASON || Boolean(trimmedNote));

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("request");
    setSelectedReason(prefillContext.reason);
    setAnotherReasonNote(prefillContext.note);
    setCompletedRequest(null);
    setTrialDetails({ schoolName: "", trialEndsOn: "", status: "" });
    setIsLoadingTrialDetails(false);
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
    setError("");

    if (!schoolid) {
      return;
    }

    let cancelled = false;
    setIsLoadingTrialDetails(true);
    Promise.allSettled([
      getSchools(),
      getActiveTrialInstancesBySchool(schoolid),
      getTrialBySchoolId(schoolid)
    ])
      .then(([schoolsResult, activeTrialsResult, legacyTrialResult]) => {
        if (cancelled) return;

        const matchingSchool = schoolsResult.status === "fulfilled"
          ? getSchoolRows(schoolsResult.value).find((school) =>
              String(school?.id || school?._id || school?.schoolid) === String(schoolid)
            )
          : null;
        const activeTrialRows = activeTrialsResult.status === "fulfilled"
          ? getTrialRows(activeTrialsResult.value)
          : [];
        const legacyTrialRows = legacyTrialResult.status === "fulfilled"
          ? getTrialRows(legacyTrialResult.value)
          : [];

        setTrialDetails(normaliseTrialDetails(matchingSchool, [...activeTrialRows, ...legacyTrialRows]));
      })
      .catch((loadError) => {
        console.error("Error loading trial details:", loadError);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTrialDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, prefillContext, schoolid]);

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setError("");

    if (reason !== ANOTHER_REASON) {
      setAnotherReasonNote("");
    }
  };

  const requestExtension = async () => {
    if (!canRequest) {
      return;
    }

    const requestPayload = {
      schoolName: displayedSchoolName,
      trialEndsOn: displayedTrialEndsOn,
      reason: selectedReason,
      note: selectedReason === ANOTHER_REASON ? trimmedNote : "",
      requestedExtensionLengthDays: 7
    };

    setIsSubmitting(true);
    setError("");

    try {
      if (onRequestExtension) {
        const overrideResponse = await onRequestExtension(requestPayload);
        setSupportReference(getSupportRequestReference(overrideResponse));
        setIsDuplicatePending(isDuplicatePendingResponse(overrideResponse));
      } else if (!schoolid) {
        setError("School context is missing. Refresh and try again.");
        setIsSubmitting(false);
        return;
      } else {
        const payload = buildTeacherSupportRequestPayload({
          type: "trial_extension",
          schoolId: schoolid,
          details: requestPayload,
          source: {
            workflow: "extend-trial"
          }
        });
        const response = await createTeacherSupportRequest(payload);
        setSupportReference(getSupportRequestReference(response));
        setIsDuplicatePending(isDuplicatePendingResponse(response));
      }

      setCompletedRequest(requestPayload);
      setStage("success");
    } catch (requestError) {
      console.error("Error requesting trial extension:", requestError);
      setError(getSupportRequestErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestClick = () => {
    if (!selectedReason) {
      return;
    }

    if (selectedReason === ANOTHER_REASON) {
      setStage("note");
      setError("");
      return;
    }

    requestExtension();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Trial extension request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard(completedRequest);
      return;
    }

    handleClose();
  };

  const renderSchoolSummary = () => (
    <div className="extend-trial-summary" aria-label="Trial summary">
      {isLoadingTrialDetails && (
        <span className="extend-trial-summary-row">
          <span>Loading</span>
          <strong className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            Trial details
          </strong>
        </span>
      )}
      <span className="extend-trial-summary-row">
        <span>Trial ends</span>
        <strong>{displayedTrialEndsOn}</strong>
      </span>
      {trialDetails.status && (
        <span className="extend-trial-summary-row">
          <span>Status</span>
          <strong>{trialDetails.status}</strong>
        </span>
      )}
    </div>
  );

  const renderReasonOption = (reasonOption) => {
    const selected = selectedReason === reasonOption.value;

    return (
      <button
        key={reasonOption.value}
        type="button"
        className={`extend-trial-reason${selected ? " selected" : ""}`}
        onClick={() => handleReasonSelect(reasonOption.value)}
        aria-pressed={selected}
      >
        <span className="extend-trial-radio" aria-hidden="true" />
        <span className="extend-trial-reason-copy">
          <strong>{reasonOption.value}</strong>
          <span>{reasonOption.detail}</span>
        </span>
      </button>
    );
  };

  const renderRequest = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Request Trial Extension</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="extend-trial-intro">
          Request more trial time for your school.
        </p>
        <Alert variant="info">
          Your request will be reviewed. Your trial end date will not change unless the extension is approved.
        </Alert>
        {error && <Alert variant="danger">{error}</Alert>}
        {renderSchoolSummary()}

        <Form.Group className="extend-trial-section" controlId="teacherExtendTrialReason">
          <Form.Label>Why do you need more time?</Form.Label>
          <div className="extend-trial-reasons" aria-label="Trial extension reasons">
            {EXTEND_TRIAL_REASONS.map(renderReasonOption)}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="extend-trial-footer-note">
          {selectedReasonOption ? `${selectedReasonOption.value} selected.` : "Choose a reason first."}
        </span>
        {onBackToTrialAccess && (
          <Button variant="outline-secondary" onClick={onBackToTrialAccess}>
            Back to Trial Access Help
          </Button>
        )}
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!selectedReason || isSubmitting} onClick={handleRequestClick}>
          {isSubmitting ? "Sending..." : "Request extension"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderNote = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Add A Short Note</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="extend-trial-intro">
          Add the reason you need more trial time.
        </p>
        <Alert variant="info">
          Your request will be reviewed. Your trial end date will not change unless the extension is approved.
        </Alert>
        {error && <Alert variant="danger">{error}</Alert>}
        {renderSchoolSummary()}

        <Form.Group className="extend-trial-section" controlId="teacherExtendTrialNote">
          <Form.Label>Note</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={anotherReasonNote}
            onChange={(event) => {
              setAnotherReasonNote(event.target.value);
              setError("");
            }}
            placeholder="Tell us why your school needs more time"
            aria-label="Trial extension note"
          />
          <Form.Text className="text-muted">
            A short note is needed for another reason.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="extend-trial-footer-note">
          {trimmedNote ? "Ready to request." : "Add a short note first."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("request")}>
          Back
        </Button>
        <Button variant="primary" disabled={!canRequest} onClick={requestExtension}>
          {isSubmitting ? "Sending..." : "Request extension"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Extension Requested</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="extend-trial-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {isDuplicatePending
                ? "Your trial extension request is already in progress."
                : "Your trial extension request has been sent."}
            </strong>
            <span>
              {supportReference ? `Reference: ${supportReference}. ` : ""}
              Trial details will be reviewed by ALPACA.
            </span>
          </span>
        </Alert>
        <div className="extend-trial-next">
          <strong>What happens next</strong>
          <span>We will review the request and update the trial details when it is handled.</span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="extend-trial-footer-note">Request sent.</span>
        {!embedded && (
          <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>
            Return to Teacher Dashboard
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Close
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-extend-trial-modal"
    >
      {stage === "request" && renderRequest()}
      {stage === "note" && renderNote()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherExtendTrialModal;
